import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { UsersIcon } from '@heroicons/react/24/outline';
import { apiCall } from '../services/api';
import toast from 'react-hot-toast';
import PermissionManagement from './PermissionManagement';
import UserDeleteConfirmation from './UserDeleteConfirmation';
import PaginatedTable from './PaginatedTable';
import ActivityLogsModal from './ActivityLogsModal';
import { useCache } from '../hooks/useCache';
import DashboardSwitcher from './DashboardSwitcher';

interface User {
  userId: string;
  role: string;
  createdAt: string;
  monthlyDataUsage: number;
  status: string;
  isEnabled: boolean;
}

interface ActivityLog {
  userId: string;
  timestamp: string;
  action: string;
  details: any;
}

interface ManagerDashboardProps {
  onSwitchToBrowser?: () => void;
  dashboardMode?: 'management' | 'browser';
}

const ManagerDashboard: React.FC<ManagerDashboardProps> = ({ 
  onSwitchToBrowser,
  dashboardMode = 'management'
}) => {
  const [activeTab, setActiveTab] = useState('users');
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(false);

  // User management state
  const [deletingUser, setDeletingUser] = useState<User | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [bulkImportData, setBulkImportData] = useState('');
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [showActivityLogs, setShowActivityLogs] = useState<string | null>(null);
  const [activityLogsLoading, setActivityLogsLoading] = useState(false);

  // Use cache for users data
  const {
    data: users = [],
    loading: usersLoading,
    refresh: refreshUsers
  } = useCache(
    'manager-users',
    async () => {
      const response = await apiCall('/admin/users');
      return Array.isArray(response) ? response : (response?.users ?? []);
    },
    { ttl: 30 * 1000 } // 30 seconds cache for manager data
  );

  const loadActivityLogs = async (userId: string) => {
    setActivityLogsLoading(true);
    setShowActivityLogs(userId);
    try {
      const response = await apiCall(`/admin/activity-logs/${userId}`);
      setActivityLogs(response);
    } catch (error) {
      toast.error('Failed to load activity logs');
      setShowActivityLogs(null);
    } finally {
      setActivityLogsLoading(false);
    }
  };

  const handleBulkImport = async () => {
    try {
      setLoading(true);
      const users = bulkImportData.split('\n').map(line => {
        const [email, role] = line.split(',');
        const userRole = role?.trim() || 'user';
        
        // Managers cannot create admin users - convert admin to manager
        const finalRole = userRole === 'admin' ? 'manager' : userRole;
        
        return { email: email?.trim(), role: finalRole };
      }).filter(user => user.email);

      // Check if any admin roles were attempted
      const hasAdminAttempts = bulkImportData.includes(',admin');
      if (hasAdminAttempts) {
        toast('Admin roles converted to Manager roles. Managers cannot create Admin users.', {
          icon: '⚠️',
          duration: 4000,
        });
      }

      const response = await apiCall('/admin/bulk-import', {
        method: 'POST',
        body: JSON.stringify({ users })
      });

      toast.success(`Bulk import completed. ${(response.results || []).filter((r: any) => r.status === 'success').length} users created.`);
      setBulkImportData('');
      setShowBulkImport(false);
      refreshUsers();
    } catch (error) {
      toast.error('Bulk import failed');
    } finally {
      setLoading(false);
    }
  };

  const toggleUserStatus = async (userId: string, currentStatus: boolean, userRole: string) => {
    try {
      // Managers cannot modify admin users
      if (userRole === 'admin') {
        toast.error('Managers cannot modify admin users');
        return;
      }
      
      await apiCall(`/admin/user-status/${userId}`, {
        method: 'PUT',
        body: JSON.stringify({ isEnabled: !currentStatus })
      });
      toast.success(`User ${!currentStatus ? 'enabled' : 'disabled'} successfully`);
      refreshUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const updateUserRole = async (userId: string, newRole: string) => {
    try {
      // Managers can only assign user and manager roles, not admin
      if (newRole === 'admin') {
        toast.error('Managers cannot assign admin role');
        return;
      }
      
      await apiCall(`/admin/users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify({ role: newRole })
      });
      toast.success('User role updated successfully');
      setEditingUser(null);
      refreshUsers();
    } catch (error) {
      toast.error('Failed to update user role');
    }
  };

  const forcePasswordReset = async (userId: string, userRole: string) => {
    try {
      // Managers cannot reset admin passwords
      if (userRole === 'admin') {
        toast.error('Managers cannot reset admin user passwords');
        return;
      }
      
      await apiCall(`/admin/password-reset/${userId}`, { method: 'POST' });
      toast.success('Password reset initiated. User will receive reset instructions via email.');
      refreshUsers(); // Refresh to show updated status
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const handleDeleteUser = async () => {
    if (!deletingUser) return;

    try {
      // Managers cannot delete admin users
      if (deletingUser.role === 'admin') {
        toast.error('Managers cannot delete admin users');
        setDeletingUser(null);
        return;
      }

      await apiCall(`/admin/users/${deletingUser.userId}`, {
        method: 'DELETE'
      });
      toast.success('User deleted successfully');
      setDeletingUser(null);
      refreshUsers();
    } catch (error) {
      toast.error('Failed to delete user');
      setDeletingUser(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <h1 className="text-3xl font-bold text-gray-900">Manager Dashboard</h1>
            <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
              Manager Access
            </div>
          </div>
          {onSwitchToBrowser && (
            <DashboardSwitcher
              currentMode={dashboardMode}
              userRole="manager"
              onSwitchToBrowser={onSwitchToBrowser}
            />
          )}
        </div>

        {/* Tab Navigation - Limited to User and Permission Management */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'users', name: 'User Management' },
              { id: 'permissions', name: 'Permission Management' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* User Management Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">User Management</h2>
              <div className="flex space-x-3">
                <div className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-md text-sm">
                  Limited Manager Access - Cannot manage Admin users
                </div>
                <button
                  onClick={() => setShowBulkImport(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                >
                  Bulk Import Users
                </button>
              </div>
            </div>

            {/* Bulk Import Modal */}
            {showBulkImport && ReactDOM.createPortal(
              <AnimatePresence>
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                  <motion.div
                    key="bulk-import-modal"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden"
                  >
                  <div className="overflow-y-auto max-h-[90vh] p-8">
                  <div className="text-center mb-6">
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
                      <UsersIcon className="h-6 w-6 text-blue-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900">Bulk Import Users</h3>
                    <p className="text-sm text-gray-600 mt-1">One entry per line: email,role</p>
                  </div>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4">
                    <p className="text-sm text-yellow-700">
                      As a manager, you can only create users with 'user' or 'manager' roles.
                    </p>
                  </div>
                  <div className="space-y-1 mb-4">
                    <label htmlFor="manager-bulk-import-data" className="block text-sm font-medium text-gray-700">
                      User list
                    </label>
                    <textarea
                      id="manager-bulk-import-data"
                      value={bulkImportData}
                      onChange={(e) => setBulkImportData(e.target.value)}
                      placeholder="email1@example.com,user&#10;email2@example.com,manager"
                      className="w-full h-32 mt-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">Format: one <code>email,role</code> pair per line.</p>
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => setShowBulkImport(false)}
                      disabled={loading}
                      className="flex-1 px-4 py-2 text-gray-700 bg-gray-300 hover:bg-gray-400 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleBulkImport}
                      disabled={loading}
                      className="flex-1 bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center justify-center"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" role="status" aria-label="Loading"></div>
                          Importing...
                        </>
                      ) : 'Import'}
                    </button>
                  </div>
                  </div>
                </motion.div>
              </div>
              </AnimatePresence>
            , document.body)}

            {/* Manager Access Notice */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    <strong>Manager Access:</strong> You can create and manage users with 'user' and 'manager' roles only. 
                    Admin users are shown but cannot be modified.
                  </p>
                </div>
              </div>
            </div>

            {/* Users Table with Manager Restrictions */}
            <PaginatedTable
              data={users || []}
              columns={[
                {
                  key: 'userId',
                  header: 'User',
                  sortable: true,
                  render: (user: User) => (
                    <div>
                      <div className="text-sm font-medium text-gray-900">{user.userId}</div>
                      <div className="text-sm text-gray-500">Created: {new Date(user.createdAt).toLocaleDateString()}</div>
                    </div>
                  )
                },
                {
                  key: 'role',
                  header: 'Role',
                  sortable: true,
                  render: (user: User) => (
                    editingUser?.userId === user.userId ? (
                      <select
                        value={editingUser.role}
                        onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                        className="text-xs border rounded px-2 py-1"
                      >
                        <option value="user">User</option>
                        <option value="manager">Manager</option>
                        {/* Managers cannot assign admin role */}
                      </select>
                    ) : (
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        user.role === 'admin' ? 'bg-red-100 text-red-800' : 
                        user.role === 'manager' ? 'bg-purple-100 text-purple-800' : 
                        'bg-green-100 text-green-800'
                      }`}>
                        {user.role}
                      </span>
                    )
                  )
                },
                {
                  key: 'isEnabled',
                  header: 'Status',
                  sortable: true,
                  render: (user: User) => (
                    <div className="flex flex-col space-y-1">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${user.isEnabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                        {user.isEnabled ? 'Active' : 'Disabled'}
                      </span>
                      {user.status === 'FORCE_CHANGE_PASSWORD' && (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                          Password Reset Required
                        </span>
                      )}
                    </div>
                  )
                },
                {
                  key: 'monthlyDataUsage',
                  header: 'Data Usage',
                  sortable: true,
                  render: (user: User) => {
                    const totalBytes = user.monthlyDataUsage || 0;
                    const formatSize = (bytes: number) => {
                      if (bytes >= 1024 * 1024 * 1024) {
                        return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
                      }
                      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
                    };
                    return (
                      <span className="text-sm text-gray-900">
                        {formatSize(totalBytes)}
                      </span>
                    );
                  }
                }
              ]}
              loading={usersLoading}
              pageSize={15}
              searchPlaceholder="Search users by ID, role, status..."
              emptyMessage="No users found"
              actions={(user: User) => (
                <div className="flex items-center space-x-2">
                  {user.role === 'admin' ? (
                    <span className="text-gray-400 text-sm italic">Admin - No Access</span>
                  ) : (
                    <>
                      {editingUser?.userId === user.userId ? (
                        <>
                          <button
                            onClick={() => updateUserRole(user.userId, editingUser.role)}
                            className="text-green-600 hover:text-green-900 text-sm font-medium"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingUser(null)}
                            className="text-gray-600 hover:text-gray-900 text-sm font-medium"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => setEditingUser(user)}
                            className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => toggleUserStatus(user.userId, user.isEnabled, user.role)}
                            className={`text-sm font-medium ${user.isEnabled ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}`}
                          >
                            {user.isEnabled ? 'Disable' : 'Enable'}
                          </button>
                          <button
                            onClick={() => forcePasswordReset(user.userId, user.role)}
                            className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                          >
                            Reset Password
                          </button>
                          <button
                            onClick={() => loadActivityLogs(user.userId)}
                            className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                          >
                            View Logs
                          </button>
                          <button
                            onClick={() => setDeletingUser(user)}
                            className="text-red-600 hover:text-red-900 text-sm font-medium"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </>
                  )}
                </div>
              )}
            />
          </div>
        )}

        {/* Permission Management Tab */}
        {activeTab === 'permissions' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Permission Management</h2>
              <div className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-md text-sm">
                Manager Access - Limited Permissions
              </div>
            </div>
            <PermissionManagement />
          </div>
        )}

        {/* User Delete Confirmation */}
        <UserDeleteConfirmation
          user={deletingUser}
          onConfirm={handleDeleteUser}
          onCancel={() => setDeletingUser(null)}
          isOpen={!!deletingUser}
        />

        {/* Activity Logs Modal */}
        <ActivityLogsModal
          isOpen={!!showActivityLogs}
          onClose={() => {
            setShowActivityLogs(null);
            setActivityLogs([]);
          }}
          userId={showActivityLogs || ''}
          activityLogs={activityLogs}
          loading={activityLogsLoading}
        />
      </div>
    </div>
  );
};

export default ManagerDashboard;